import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Calendar,
  Clock,
  Database,
  Search,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Loader2,
  Server,
  Activity,
  Copy,
  Check,
  Zap,
  PlayCircle,
  Timer,
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { CustomDropdown } from '../../../components/CustomDropdown ';

interface MySQLScheduledJobsViewProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

interface ScheduledJobItem {
  agent_id?: number;
  asset_id?: number;
  client_id?: number;
  cluster_id?: string;
  collected_at?: string;
  config_id?: string;
  database?: string;
  db_type?: string;
  enabled?: boolean;
  hostname?: string;
  indexed_at?: string;
  ip?: string;
  last_run_at?: string;
  log_timestamp?: string;
  master_agent_id?: string;
  name?: string;
  organization_id?: number;
  os?: string;
  schedule?: string;
}

export const MySQLScheduledJobsView: React.FC<MySQLScheduledJobsViewProps> = ({
  activeCluster,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [jobsData, setJobsData] = useState<ScheduledJobItem[]>([]);
  const [capability, setCapability] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedDb, setSelectedDb] = useState<string>('ALL');
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL');
  const [copiedName, setCopiedName] = useState<string | null>(null);

  const fetchJobs = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const params: any = {
      cluster_id: activeCluster,
    };
    if (clientId) {
      params.client_id = clientId;
    }

    try {
      const res = await baseapi.get('/db_monitor_dashboard/scheduled_jobs', { params });
      const rawPayload = res.data;
      const rawData = rawPayload?.data || (Array.isArray(rawPayload) ? rawPayload : []);
      setJobsData(Array.isArray(rawData) ? rawData : []);
      setCapability(rawPayload?.capability || null);
    } catch (err) {
      console.warn('MySQLScheduledJobsView fetch error:', err);
      setJobsData([]);
      setCapability(null);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, clientId]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs, refreshKey]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedName(text);
    setTimeout(() => setCopiedName(null), 1800);
  };

  const databasesList = useMemo(() => {
    const dbs = new Set<string>();
    jobsData.forEach((j) => {
      if (j.database) dbs.add(j.database);
    });
    return Array.from(dbs);
  }, [jobsData]);

  const enabledCount = jobsData.filter((j) => j.enabled === true).length;
  const disabledCount = jobsData.filter((j) => j.enabled === false).length;

  const databaseOptions = useMemo(() => [
    { label: `All Databases (${databasesList.length})`, value: 'ALL' },
    ...databasesList.map((db) => ({ label: db, value: db })),
  ], [databasesList]);

  const statusOptions = useMemo(() => [
    { label: 'All Statuses', value: 'ALL' },
    { label: `Enabled (${enabledCount})`, value: 'ENABLED' },
    { label: `Disabled (${disabledCount})`, value: 'DISABLED' },
  ], [enabledCount, disabledCount]);

  const filteredJobs = useMemo(() => {
    return jobsData.filter((job) => {
      const matchSearch =
        !searchTerm ||
        (job.name && job.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (job.database && job.database.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (job.schedule && job.schedule.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchDb = selectedDb === 'ALL' || job.database === selectedDb;
      const matchStatus =
        selectedStatus === 'ALL' ||
        (selectedStatus === 'ENABLED' && job.enabled === true) ||
        (selectedStatus === 'DISABLED' && job.enabled === false);

      return matchSearch && matchDb && matchStatus;
    });
  }, [jobsData, searchTerm, selectedDb, selectedStatus]);

  const isSchedulerEnabled =
    capability?.status === 'supported_enabled' || capability?.status?.toLowerCase().includes('enabled');

  if (loading) {
    return (
      <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl min-h-[350px]">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      {/* Top Banner Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm dark:shadow-lg">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-orange-500/10 dark:bg-orange-600/20 border border-orange-300 dark:border-orange-500/30 flex items-center justify-center text-orange-600 dark:text-orange-400 shrink-0">
            <Calendar className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white tracking-wide">
              Event Scheduler
              </h2>
              <span
                className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${
                  isSchedulerEnabled
                    ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/30'
                    : 'bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-500/30'
                }`}
              >
                  {isSchedulerEnabled ? "Scheduler ON" : "Scheduler OFF"}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Automated event triggers, recurring cron jobs, and background maintenance tasks
            </p>
          </div>
        </div>

        <button
          onClick={fetchJobs}
          type="button"
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-semibold shrink-0 transition-colors shadow-sm cursor-pointer self-start sm:self-auto"
        >
          <RefreshCw className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
          <span>Refresh</span>
        </button>
      </div>

      {/* 4 Summary Stat Tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {/* Total Events */}
        <div className="bg-blue-50/70 dark:bg-blue-600/10 border border-blue-200 dark:border-blue-500/30 rounded-xl p-3 shadow-sm dark:shadow-md">
          <div className="flex items-center justify-between text-[11px] text-gray-600 dark:text-gray-400 font-semibold mb-1">
            <span>Total Events</span>
            <Activity className="w-4 h-4 text-blue-500 dark:text-blue-400" />
          </div>
          <div className="text-xl font-bold font-mono text-gray-900 dark:text-white">{jobsData.length}</div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Across {databasesList.length} database(s)</div>
        </div>

        {/* Enabled & Active */}
        <div className="bg-emerald-50/70 dark:bg-emerald-600/10 border border-emerald-200 dark:border-emerald-500/30 rounded-xl p-3 shadow-sm dark:shadow-md">
          <div className="flex items-center justify-between text-[11px] text-gray-600 dark:text-gray-400 font-semibold mb-1">
            <span>Active / Enabled</span>
            <CheckCircle2 className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
          </div>
          <div className="text-xl font-bold font-mono text-emerald-600 dark:text-emerald-400">{enabledCount}</div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Actively scheduled</div>
        </div>

        {/* Disabled Events */}
        <div className="bg-gray-50/70 dark:bg-gray-600/10 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3 shadow-sm dark:shadow-md">
          <div className="flex items-center justify-between text-[11px] text-gray-600 dark:text-gray-400 font-semibold mb-1">
            <span>Disabled Events</span>
            <XCircle className="w-4 h-4 text-gray-400" />
          </div>
          <div className="text-xl font-bold font-mono text-gray-700 dark:text-gray-300">{disabledCount}</div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Inactive / Paused</div>
        </div>

        {/* Scheduler Mechanism */}
        <div className="bg-purple-50/70 dark:bg-purple-600/10 border border-purple-200 dark:border-purple-500/30 rounded-xl p-3 shadow-sm dark:shadow-md">
          <div className="flex items-center justify-between text-[11px] text-gray-600 dark:text-gray-400 font-semibold mb-1">
            <span>Mechanism</span>
            <Zap className="w-4 h-4 text-purple-500 dark:text-purple-400" />
          </div>
          <div className="text-sm font-bold text-purple-700 dark:text-purple-300 capitalize truncate">
            {capability?.mechanism ? capability.mechanism.replace(/_/g, ' ') : 'Event Scheduler'}
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl space-y-3.5">
        {/* Table Controls (Search & Filters) */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5">
          {/* Search Box */}
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by event name, database, schedule..."
              className="w-full bg-gray-50 dark:bg-gray-900/80 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors h-9"
            />
          </div>

          {/* Filters Row */}
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
            {/* Database Filter CustomDropdown */}
            {databasesList.length > 0 && (
              <CustomDropdown
                data={databaseOptions}
                selectedValue={selectedDb}
                onSelect={(val) => setSelectedDb(val)}
                width="w-44 sm:w-48"
                placeholder="Database"
                buttonHeight={9}
              />
            )}

            {/* Status Filter CustomDropdown */}
            <CustomDropdown
              data={statusOptions}
              selectedValue={selectedStatus}
              onSelect={(val) => setSelectedStatus(val)}
              width="w-36 sm:w-40"
              placeholder="Status"
              buttonHeight={9}
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto custom-scroll rounded-lg border border-gray-200 dark:border-gray-700/50">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-900/90 border-b border-gray-200 dark:border-gray-700 text-[11px] text-gray-600 dark:text-gray-400 uppercase tracking-wider font-semibold whitespace-nowrap">
                <th className="py-2.5 px-3.5">Status</th>
                <th className="py-2.5 px-3.5">Event Name</th>
                <th className="py-2.5 px-3.5">Database</th>
                <th className="py-2.5 px-3.5">Schedule / Interval</th>
                <th className="py-2.5 px-3.5">Last Run At</th>
                <th className="py-2.5 px-3.5">Host / Node</th>

              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30 text-gray-800 dark:text-gray-200">
              {filteredJobs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-500 dark:text-gray-400 text-xs italic">
                    {jobsData.length === 0 ? 'Scheduler is OFF 0r No events defined' : 'No matching events found'}
                  </td>
                </tr>
              ) : (
                filteredJobs.map((job, idx) => {
                  const isEnabled = job.enabled === true;
                  const isCopied = copiedName === job.name;

                  return (
                    <tr
                      key={idx}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors font-mono whitespace-nowrap"
                    >
                      {/* Status */}
                      <td className="py-2.5 px-3.5">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${
                            isEnabled
                              ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/30'
                              : 'bg-gray-100 dark:bg-gray-700/40 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600/40'
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              isEnabled ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'
                            }`}
                          />
                          {isEnabled ? 'ENABLED' : 'DISABLED'}
                        </span>
                      </td>

                      {/* Event Name */}
                      <td className="py-2.5 px-3.5 font-bold text-gray-900 dark:text-white">
                        <div className="flex items-center gap-2 group">
                          <PlayCircle className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400 shrink-0" />
                          <span className="font-semibold text-gray-900 dark:text-white tracking-tight">{job.name}</span>
                          {job.name && (
                            <button
                              onClick={() => handleCopy(job.name!)}
                              type="button"
                              className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-700 dark:hover:text-white transition-opacity cursor-pointer"
                              title="Copy event name"
                            >
                              {isCopied ? <Check className="w-3 h-3 text-emerald-500 dark:text-emerald-400" /> : <Copy className="w-3 h-3" />}
                            </button>
                          )}
                        </div>
                      </td>

                      {/* Database */}
                      <td className="py-2.5 px-3.5">
                        <div className="flex items-center gap-1.5">
                          <Database className="w-3.5 h-3.5 text-cyan-500 dark:text-cyan-400 shrink-0" />
                          <span className="px-2 py-0.5 rounded bg-cyan-50 dark:bg-gray-900/80 border border-cyan-200 dark:border-gray-700 text-cyan-700 dark:text-cyan-300 font-semibold text-[11px]">
                            {job.database || 'default'}
                          </span>
                        </div>
                      </td>

                      {/* Schedule */}
                      <td className="py-2.5 px-3.5">
                        <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-200">
                          <Timer className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400 shrink-0" />
                          <span className="font-semibold text-purple-700 dark:text-purple-300 font-sans text-xs">
                            {job.schedule || '—'}
                          </span>
                        </div>
                      </td>

                      {/* Last Run At */}
                      <td className="py-2.5 px-3.5">
                        {job.last_run_at ? (
                          <div className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300">
                            <Clock className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400 shrink-0" />
                            <span>{job.last_run_at}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500 italic">Not run yet</span>
                        )}
                      </td>

                      {/* Host / Node */}
                      <td className="py-2.5 px-3.5 text-gray-600 dark:text-gray-300">
                        <div className="flex items-center gap-1.5">
                          <Server className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          <span title={job.config_id || job.ip}>
                            {job.hostname || job.ip || job.config_id || 'localhost'}
                          </span>
                        </div>
                      </td>
                     
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MySQLScheduledJobsView;
